import { useState, useEffect, useMemo } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { 
  getAnalyticsDashboard,
  getHealthScore,
  getSavingsRecommendations,
  getCashFlowForecast,
  getAnomalyAlerts
} from '../lib/api';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  Lightbulb,
  PiggyBank,
  BarChart3,
  Activity,
  Shield,
  Loader2,
  ArrowUp,
  ArrowDown,
  DollarSign,
  Calendar,
  Target,
  Zap
} from 'lucide-react';

const AnalyticsDashboard = () => {
  const [dashboard, setDashboard] = useState(null);
  const [healthScore, setHealthScore] = useState(null);
  const [loading, setLoading] = useState(true);

  // Memoize expensive computations
  const sortedCategoryPredictions = useMemo(() => {
    if (!dashboard?.spending_predictions?.breakdown_by_category) return [];
    return Object.entries(dashboard.spending_predictions.breakdown_by_category)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  }, [dashboard?.spending_predictions?.breakdown_by_category]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [dashboardRes, healthRes] = await Promise.all([
        getAnalyticsDashboard(),
        getHealthScore()
      ]);
      setDashboard(dashboardRes.data);
      setHealthScore(healthRes.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-emerald-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBg = (score) => {
    if (score >= 80) return 'bg-emerald-500';
    if (score >= 60) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'good':
        return <span className="px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 text-xs">Good</span>;
      case 'warning':
        return <span className="px-2 py-0.5 rounded-full bg-yellow-500/10 text-yellow-600 text-xs">Warning</span>;
      case 'alert':
        return <span className="px-2 py-0.5 rounded-full bg-red-500/10 text-red-600 text-xs">Alert</span>;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </div>
      </Layout>
    );
  }

  if (!dashboard) {
    return (
      <Layout>
        <div className="text-center py-12">
          <BarChart3 className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-xl font-heading font-bold">No Analytics Data</h2>
          <p className="text-muted-foreground">Start making transactions to see your analytics</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <motion.div 
        className="space-y-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        data-testid="analytics-dashboard"
      >
        {/* Header */}
        <div>
          <h1 className="text-3xl font-heading font-bold flex items-center gap-3">
            <BarChart3 className="w-8 h-8 text-emerald-600" />
            Smart Analytics
          </h1>
          <p className="text-muted-foreground">AI-powered insights and predictions for your finances</p>
        </div>

        {/* Health Score Card */}
        <Card className="overflow-hidden">
          <CardContent className="p-0">
            <div className="grid md:grid-cols-5 gap-0">
              {/* Main Score */}
              <div className="md:col-span-2 p-8 flex flex-col items-center justify-center bg-gradient-to-br from-emerald-500/10 to-emerald-500/5">
                <p className="text-sm text-muted-foreground mb-2">Financial Health Score</p>
                <div className="relative">
                  <svg className="w-40 h-40" viewBox="0 0 100 100">
                    <circle
                      cx="50"
                      cy="50"
                      r="45"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="8"
                      className="text-muted/20"
                    />
                    <circle
                      cx="50"
                      cy="50"
                      r="45"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="8"
                      strokeDasharray={`${(dashboard.spending_health_score / 100) * 283} 283`}
                      strokeLinecap="round"
                      transform="rotate(-90 50 50)"
                      className={getScoreColor(dashboard.spending_health_score)}
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className={`text-4xl font-bold ${getScoreColor(dashboard.spending_health_score)}`}>
                      {dashboard.spending_health_score}
                    </span>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  {dashboard.spending_health_score >= 80 ? 'Excellent' : 
                   dashboard.spending_health_score >= 60 ? 'Good' : 'Needs Attention'}
                </p>
              </div>

              {/* Score Breakdown */}
              <div className="md:col-span-3 p-6 space-y-4">
                <h3 className="font-heading font-semibold mb-4">Score Breakdown</h3>
                {healthScore?.breakdown && Object.entries(healthScore.breakdown).map(([key, value]) => (
                  <div key={key} className="space-y-1">
                    <div className="flex justify-between items-center">
                      <span className="text-sm capitalize">{key.replace(/_/g, ' ')}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{value.score}/{value.max}</span>
                        {getStatusBadge(value.status)}
                      </div>
                    </div>
                    <Progress value={(value.score / value.max) * 100} className="h-2" />
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Month over Month</p>
                  <p className={`text-2xl font-bold flex items-center gap-1 ${
                    dashboard.month_over_month_change >= 0 ? 'text-red-600' : 'text-emerald-600'
                  }`}>
                    {dashboard.month_over_month_change >= 0 ? (
                      <ArrowUp className="w-5 h-5" />
                    ) : (
                      <ArrowDown className="w-5 h-5" />
                    )}
                    {Math.abs(dashboard.month_over_month_change).toFixed(1)}%
                  </p>
                </div>
                <div className={`p-3 rounded-xl ${
                  dashboard.month_over_month_change >= 0 ? 'bg-red-500/10' : 'bg-emerald-500/10'
                }`}>
                  {dashboard.month_over_month_change >= 0 ? (
                    <TrendingUp className="w-6 h-6 text-red-600" />
                  ) : (
                    <TrendingDown className="w-6 h-6 text-emerald-600" />
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Predicted Next Month</p>
                  <p className="text-2xl font-bold">
                    ${dashboard.spending_predictions?.predicted_amount?.toFixed(2) || '0'}
                  </p>
                </div>
                <div className="p-3 rounded-xl bg-purple-500/10">
                  <Activity className="w-6 h-6 text-purple-600" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {(dashboard.spending_predictions?.confidence * 100).toFixed(0)}% confidence
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Potential Savings</p>
                  <p className="text-2xl font-bold text-emerald-600">
                    ${dashboard.savings_recommendations?.reduce((sum, r) => sum + r.potential_savings, 0).toFixed(2) || '0'}
                  </p>
                </div>
                <div className="p-3 rounded-xl bg-emerald-500/10">
                  <PiggyBank className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="insights" className="w-full">
          <TabsList className="grid w-full grid-cols-4 h-12 rounded-xl">
            <TabsTrigger value="insights" className="rounded-lg">
              <Lightbulb className="w-4 h-4 mr-2" />
              Insights
            </TabsTrigger>
            <TabsTrigger value="recommendations" className="rounded-lg">
              <Target className="w-4 h-4 mr-2" />
              Savings Tips
            </TabsTrigger>
            <TabsTrigger value="forecast" className="rounded-lg">
              <Calendar className="w-4 h-4 mr-2" />
              Cash Flow
            </TabsTrigger>
            <TabsTrigger value="alerts" className="rounded-lg">
              <Shield className="w-4 h-4 mr-2" />
              Alerts
            </TabsTrigger>
          </TabsList>

          {/* Insights Tab */}
          <TabsContent value="insights" className="space-y-4 mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Lightbulb className="w-5 h-5 text-yellow-500" />
                  AI Insights
                </CardTitle>
                <CardDescription>Personalized observations about your spending</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {dashboard.insights?.map((insight, idx) => (
                    <li key={`insight-${idx}-${insight.slice(0, 20)}`} className="flex items-start gap-3 p-3 rounded-lg bg-muted">
                      <Zap className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                      <span>{insight}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Spending by Category Prediction */}
            {sortedCategoryPredictions.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading">Predicted Spending by Category</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {sortedCategoryPredictions.map(([category, amount]) => (
                        <div key={category} className="flex items-center justify-between">
                          <span className="text-sm">{category}</span>
                          <span className="font-medium">${amount.toFixed(2)}</span>
                        </div>
                      ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Savings Recommendations Tab */}
          <TabsContent value="recommendations" className="space-y-4 mt-6">
            {dashboard.savings_recommendations?.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <PiggyBank className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No savings recommendations yet</p>
                </CardContent>
              </Card>
            ) : (
              dashboard.savings_recommendations?.map((rec) => (
                <Card key={rec.id}>
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold">{rec.category}</h3>
                          <span className={`px-2 py-0.5 rounded-full text-xs ${
                            rec.impact === 'high' ? 'bg-emerald-500/10 text-emerald-600' :
                            rec.impact === 'medium' ? 'bg-yellow-500/10 text-yellow-600' :
                            'bg-blue-500/10 text-blue-600'
                          }`}>
                            {rec.impact} impact
                          </span>
                        </div>
                        <div className="grid grid-cols-3 gap-4 mb-4">
                          <div>
                            <p className="text-xs text-muted-foreground">Current</p>
                            <p className="font-medium">${rec.current_spending.toFixed(2)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Target</p>
                            <p className="font-medium text-emerald-600">${rec.recommended_budget.toFixed(2)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Savings</p>
                            <p className="font-bold text-emerald-600">${rec.potential_savings.toFixed(2)}</p>
                          </div>
                        </div>
                        <div className="space-y-1">
                          <p className="text-xs text-muted-foreground font-medium">Tips:</p>
                          <ul className="text-sm text-muted-foreground space-y-1">
                            {rec.tips.slice(0, 2).map((tip) => (
                              <li key={tip} className="flex items-center gap-2">
                                <CheckCircle className="w-3 h-3 text-emerald-500" />
                                {tip}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          {/* Cash Flow Forecast Tab */}
          <TabsContent value="forecast" className="space-y-4 mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-blue-500" />
                  14-Day Cash Flow Forecast
                </CardTitle>
                <CardDescription>Projected balance based on your spending patterns</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {dashboard.cash_flow_forecast?.slice(0, 7).map((day) => (
                    <div key={`forecast-${day.date}`} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                      <div>
                        <p className="font-medium">{new Date(day.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</p>
                        <p className="text-xs text-muted-foreground">
                          +${day.expected_income.toFixed(2)} / -${day.expected_expenses.toFixed(2)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className={`font-bold ${day.projected_balance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          ${day.projected_balance.toFixed(2)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {(day.confidence * 100).toFixed(0)}% confidence
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Alerts Tab */}
          <TabsContent value="alerts" className="space-y-4 mt-6">
            {dashboard.anomaly_alerts?.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <CheckCircle className="w-16 h-16 mx-auto text-emerald-500 mb-4" />
                  <h3 className="font-heading font-semibold text-lg">All Clear!</h3>
                  <p className="text-muted-foreground">No unusual activity detected</p>
                </CardContent>
              </Card>
            ) : (
              dashboard.anomaly_alerts?.map((alert) => (
                <Card key={alert.id} className={`border-l-4 ${
                  alert.severity === 'high' ? 'border-l-red-500' :
                  alert.severity === 'medium' ? 'border-l-orange-500' :
                  'border-l-yellow-500'
                }`}>
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className={`p-2 rounded-lg ${
                        alert.severity === 'high' ? 'bg-red-500/10' :
                        alert.severity === 'medium' ? 'bg-orange-500/10' :
                        'bg-yellow-500/10'
                      }`}>
                        <AlertTriangle className={`w-5 h-5 ${
                          alert.severity === 'high' ? 'text-red-600' :
                          alert.severity === 'medium' ? 'text-orange-600' :
                          'text-yellow-600'
                        }`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold capitalize">{alert.type.replace(/_/g, ' ')}</h3>
                          <span className={`px-2 py-0.5 rounded-full text-xs ${
                            alert.severity === 'high' ? 'bg-red-500/10 text-red-600' :
                            alert.severity === 'medium' ? 'bg-orange-500/10 text-orange-600' :
                            'bg-yellow-500/10 text-yellow-600'
                          }`}>
                            {alert.severity}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground">{alert.description}</p>
                        <p className="text-xs text-muted-foreground mt-2">
                          Detected: {new Date(alert.detected_at).toLocaleString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-lg">${alert.amount.toFixed(2)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>
        </Tabs>
      </motion.div>
    </Layout>
  );
};

export default AnalyticsDashboard;
